/**
 * Adapter functions for converting distro (survey.ts) dashboard components
 * to admin panel dashboard components.
 *
 * Each adapter returns an AdaptedDashboardComponent containing:
 * - The adapted component (if supported)
 * - Audit information about the adaptation
 */
import type * as survey from '@aidkitorg/types/lib/survey';
import type {
  DashboardMetric,
  DashboardBarChart,
  DashboardPieChart,
  DashboardLineChart,
  DashboardSummaryTable,
  DashboardReport,
  DashboardExplanation,
  SupportedDashboardComponent,
} from '@aidkitorg/types/lib/admin-panel/dashboard';
import type {
  AdaptedDashboardComponent,
  ComponentAuditResult,
  ComponentMigrationStatus,
} from '@aidkitorg/types/lib/admin-panel/distro-dashboard-migration';
import { expandGenericTemplates } from '@aidkitorg/types/lib/translation/templates';

// Re-export for convenience
export type {
  AdaptedDashboardComponent,
  PrecomputedCustomQueryData,
} from '@aidkitorg/types/lib/admin-panel/distro-dashboard-migration';

/**
 * Sanitize backend error strings into user-friendly messages.
 * Known errors get descriptive messages; unknown errors get a safe generic fallback.
 */
function sanitizeDashboardError(error: string): string {
  const lower = error.toLowerCase();

  // Timeout-related
  if (lower.includes('statement timeout') || lower.includes('query cancelled') || lower.includes('query canceled')) {
    return 'Query timed out. Try simplifying the query or adding filters to reduce the data set.';
  }

  // Connection/availability issues
  if (lower.includes('connection') || lower.includes('server closed') || lower.includes('broken pipe')) {
    return 'Database connection was lost. Please try refreshing the dashboard.';
  }

  // Lock timeout
  if (lower.includes('lock timeout')) {
    return 'Query could not acquire a database lock. Try again in a few minutes.';
  }

  // Background process (already a friendly message from backend)
  if (lower.includes('background process')) {
    return error;
  }

  // Invalid query structure (already friendly messages from backend)
  if (lower.includes('invalid count query')) {
    return error;
  }

  // Generic fallback — don't leak raw Postgres errors
  return 'Query error. Please check the query syntax and try again.';
}

/**
 * Helper to extract string title from Text | string
 */
function extractTitle(title: string | survey.Text): string {
  if (typeof title === 'string') return title;
  return title?.en || 'Untitled';
}

/**
 * Derive display header names from an ApplicantTable's static columns config.
 * Used so we can render table headers even when results is an empty array.
 */
export function extractApplicantTableHeaders(
  columns: survey.ApplicantTable['columns'],
  options?: { hideUidCopyButton?: boolean },
): string[] {
  // Backend returns a `uid` column for applicant rows unless hideUidCopyButton
  // is set; mirror that here so empty-state headers match populated-state headers.
  const headers: string[] = options?.hideUidCopyButton ? [] : ['uid'];
  for (const col of columns) {
    const anyCol = col as any;
    let label: string | undefined;
    switch (anyCol.kind) {
      case 'Named Expression':
        label = anyCol.name ? extractTitle(anyCol.name) : anyCol.expression?.kind;
        break;
      case 'Table When':
      case 'ApplicantCreatorButton':
        label = anyCol.columnHeader ? extractTitle(anyCol.columnHeader) : '';
        break;
      case 'NotificationSender':
        label = typeof anyCol.field === 'string' ? anyCol.field : (anyCol.field?.field ?? '');
        break;
      case 'Table Button':
        label = anyCol.button?.label ? extractTitle(anyCol.button.label) : '';
        break;
      default:
        // Plain ValueExpr: best-effort label from common shapes
        label = anyCol.field ?? anyCol.name ?? anyCol.kind ?? '';
    }
    headers.push(label ?? '');
  }
  return headers;
}

/**
 * Create audit result for a component
 */
function createAudit(
  componentIndex: number,
  componentKind: string,
  status: ComponentMigrationStatus,
  supportedFeatures: string[] = [],
  unsupportedFeatures: string[] = [],
  message?: string
): ComponentAuditResult {
  return {
    componentIndex,
    componentKind,
    status,
    supportedFeatures,
    unsupportedFeatures,
    message,
  };
}

/**
 * Create a download-only adaptation for components that only support CSV download (no inline view).
 */
function createDownloadOnlyAdaptation(
  component: survey.DashboardComponent,
  componentIndex: number,
  description: string
): AdaptedDashboardComponent {
  return {
    original: component,
    adapted: null,
    audit: createAudit(componentIndex, 'Download', 'partially_supported',
      ['title', 'download'], ['inline table view'],
      description),
  };
}

/**
 * Adapt HeroNumber/HeroNumberWithFields to DashboardMetric
 */
export function adaptHeroNumber(
  component: survey.HeroNumberWithFields,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'summary', 'filter', 'prefix', 'suffix', 'useCommas', 'decimalPlaces', 'description'];
  const unsupportedFeatures: string[] = [];

  const adapted: DashboardMetric = {
    kind: 'Hero Number',
    title: component.title,
    summary: component.summary,
    filter: component.filter,
    prefix: component.prefix,
    suffix: component.suffix,
    useCommas: component.useCommas,
    decimalPlaces: component.decimalPlaces,
    description: component.description,
  };

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Hero Number',
      'fully_supported',
      supportedFeatures,
      unsupportedFeatures
    ),
  };
}

/**
 * Adapt BarChart/BarChartWithFields to DashboardBarChart
 */
export function adaptBarChart(
  component: survey.BarChartWithFields,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'summary', 'groups', 'filter', 'countEmpty', 'allowCommasInGroups', 'bars', 'order', 'description', 'xAxisLabel', 'yAxisLabel', 'showPercentages', 'units'];
  const unsupportedFeatures: string[] = [];

  // Check for unsupported features
  if (component.includeNonLatest) {
    unsupportedFeatures.push('includeNonLatest');
  }

  const adapted: DashboardBarChart = {
    kind: 'Bar Chart',
    title: component.title,
    summary: component.summary,
    groups: component.groups,
    filter: component.filter,
    countEmpty: component.countEmpty,
    allowCommasInGroups: component.allowCommasInGroups,
    bars: component.bars,
    order: component.order,
    description: component.description,
    xAxisLabel: component.xAxisLabel,
    yAxisLabel: component.yAxisLabel,
    showPercentages: component.showPercentages,
    hideTotal: component.hideTotal,
    numberFormat: component.numberFormat,
    showBarLabels: component.showBarLabels,
    units: component.units,
  };

  const status: ComponentMigrationStatus = unsupportedFeatures.length > 0
    ? 'partially_supported'
    : 'fully_supported';

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Bar Chart',
      status,
      supportedFeatures,
      unsupportedFeatures,
      unsupportedFeatures.length > 0 ? `Some features not migrated: ${unsupportedFeatures.join(', ')}` : undefined
    ),
  };
}

/**
 * Adapt PieChart/PieChartWithFields to DashboardPieChart
 */
export function adaptPieChart(
  component: survey.PieChartWithFields,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'summary', 'groups', 'filter', 'countEmpty', 'allowCommasInGroups', 'showLegend', 'description', 'showPercentages'];
  const unsupportedFeatures: string[] = [];

  // Check for unsupported features
  if (component.width) {
    unsupportedFeatures.push('width (custom sizing)');
  }
  if (component.height) {
    unsupportedFeatures.push('height (custom sizing)');
  }
  if (component.pieStyle) {
    unsupportedFeatures.push('pieStyle');
  }

  const adapted: DashboardPieChart = {
    kind: 'Pie Chart',
    title: component.title,
    summary: component.summary,
    groups: component.groups,
    filter: component.filter,
    countEmpty: component.countEmpty,
    allowCommasInGroups: component.allowCommasInGroups,
    showLegend: component.showLegend,
    description: component.description,
    showPercentages: component.showPercentages,
  };

  const status: ComponentMigrationStatus = unsupportedFeatures.length > 0
    ? 'partially_supported'
    : 'fully_supported';

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Pie Chart',
      status,
      supportedFeatures,
      unsupportedFeatures,
      unsupportedFeatures.length > 0 ? `Some features not migrated: ${unsupportedFeatures.join(', ')}` : undefined
    ),
  };
}

/**
 * Adapt LineChart to DashboardLineChart
 */
export function adaptLineChart(
  component: survey.LineChart,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'summary', 'groups', 'filter', 'description', 'xAxisLabel', 'yAxisLabel', 'showPercentages'];
  const unsupportedFeatures: string[] = [];

  const adapted: DashboardLineChart = {
    kind: 'Line Chart',
    title: component.title,
    summary: component.summary,
    groups: component.groups,
    filter: component.filter,
    description: component.description,
    xAxisLabel: component.xAxisLabel,
    yAxisLabel: component.yAxisLabel,
    showPercentages: component.showPercentages,
  };

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Line Chart',
      'fully_supported',
      supportedFeatures,
      unsupportedFeatures
    ),
  };
}

/**
 * Adapt SummaryTable to DashboardSummaryTable
 */
export function adaptSummaryTable(
  component: survey.SummaryTable,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'summary', 'groups', 'filter', 'description', 'showPercentages'];
  const unsupportedFeatures: string[] = [];

  const adapted: DashboardSummaryTable = {
    kind: 'Summary Table',
    title: component.title,
    summary: component.summary,
    groups: component.groups,
    filter: component.filter,
    description: component.description,
    showPercentages: component.showPercentages,
  };

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Summary Table',
      'fully_supported',
      supportedFeatures,
      unsupportedFeatures
    ),
  };
}

/**
 * Adapt Map to admin panel Map
 */
export function adaptMap(
  component: survey.Map,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['title', 'sources', 'filter', 'center', 'zoom', 'legend', 'description'];
  const unsupportedFeatures: string[] = [];

  // Map component is directly compatible
  const adapted: survey.Map = { ...component };

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Map',
      'fully_supported',
      supportedFeatures,
      unsupportedFeatures
    ),
  };
}

/**
 * Extended HeroCount type that may include pre-computed results from the distro dashboard API.
 */
type HeroCountWithResults = survey.HeroCountWithFields & {
  results?: number;
  error?: string;
};

/**
 * Adapt HeroCount to use pre-computed results.
 * Hero Count returns a single number (rowCount) from the backend.
 */
export function adaptHeroCount(
  component: HeroCountWithResults,
  componentIndex: number
): AdaptedDashboardComponent {
  if (component.error) {
    return createQueryErrorResult(component, componentIndex, 'Hero Count', component.error);
  }

  const hasResults = component.results !== undefined && component.results !== null;

  if (hasResults) {
    const supportedFeatures = ['title', 'count result', 'prefix', 'suffix', 'useCommas', 'decimalPlaces'];
    const unsupportedFeatures = ['filter (already executed)', 'useOfflineData'];

    // Create a visualization config compatible with CustomNumber for rendering
    const visualization: survey.CustomNumber = {
      kind: 'Custom Number' as const,
      title: typeof component.title === 'string' ? { en: component.title } : component.title,
      prefix: component.prefix,
      suffix: component.suffix,
      useCommas: component.useCommas,
      decimalPlaces: component.decimalPlaces,
    };

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        'Hero Count',
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered using pre-computed count result.'
      ),
      precomputedData: {
        results: component.results!, // We checked hasResults above
        visualization,
        title: extractTitle(typeof component.title === 'string' ? component.title : component.title || 'Count'),
      },
    };
  }

  // No results available
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      'Hero Count',
      'unsupported',
      [],
      ['no pre-computed results'],
      'Hero Count requires pre-computed results from the dashboard API. Results not available.'
    ),
  };
}

/**
 * Extended ApplicantTable type that may include pre-computed results from the distro dashboard API.
 */
type ApplicantTableWithResults = survey.ApplicantTable & {
  results?: any[];
  error?: string;
};

/**
 * Adapt ApplicantTable to use pre-computed results.
 * ApplicantTable returns an array of row objects from the backend.
 */
export function adaptApplicantTable(
  component: ApplicantTableWithResults,
  componentIndex: number
): AdaptedDashboardComponent {
  if (component.error) {
    return createQueryErrorResult(component, componentIndex, 'Applicant Table', component.error);
  }

  // Download-only Applicant Table — no inline table, just a download card
  if (component.download?.downloadOnly) {
    return createDownloadOnlyAdaptation(component, componentIndex,
      'Download-only Applicant Table. Data available via CSV download.');
  }

  const hasResults = Array.isArray(component.results);

  if (hasResults) {
    const supportedFeatures = ['title', 'results data', 'columns', 'view button', 'linksToSection', 'download'];
    const unsupportedFeatures = ['filter (already executed)', 'showAllSimultaneously'];

    // Create a Table-like visualization config for rendering
    const visualization: survey.Table = {
      kind: 'Table',
      title: component.title,
      hideViewButton: component.hideViewButton,
      hideTotalCount: component.hideTotalCount,
      hideUidCopyButton: component.hideUidCopyButton,
      linksToSection: component.linksToSection,
    };

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        'Applicant Table',
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered as a table using pre-computed results.'
      ),
      precomputedData: {
        results: component.results!,
        visualization,
        title: extractTitle(component.title || 'Applicant Table'),
        staticColumnHeaders: extractApplicantTableHeaders(component.columns, { hideUidCopyButton: component.hideUidCopyButton }),
      },
      download: component.download ? {
        filename: component.download.filename,
        downloadOnly: component.download.downloadOnly,
        asyncDownload: component.download.asyncDownload,
        downloadPath: component.download.downloadPath,
      } : undefined,
    };
  }

  // No results available — provide actionable guidance
  console.log(
    `[adaptApplicantTable] Applicant Table "${extractTitle(component.title || 'Untitled')}" ` +
    `(index ${componentIndex}) has no results. Data will be loaded per-component via /dashboard/load.`
  );
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      'Applicant Table',
      'unsupported',
      [],
      ['no pre-computed results'],
      'Applicant Table was found but has no query results. The backend query may have failed — check the browser console and network tab for errors.'
    ),
    download: component.download ? {
      filename: component.download.filename,
      downloadOnly: component.download.downloadOnly,
      asyncDownload: component.download.asyncDownload,
      downloadPath: component.download.downloadPath,
    } : undefined,
  };
}

/**
 * Extended MapRegionDensity type that may include pre-computed results from the distro dashboard API.
 */
type MapRegionDensityWithResults = survey.MapRegionDensity & {
  results?: Array<{ geojson: any }>;
};

/**
 * Adapt MapRegionDensity to use pre-computed results.
 * MapRegionDensity returns GeoJSON data for choropleth map rendering.
 */
export function adaptMapRegionDensity(
  component: MapRegionDensityWithResults,
  componentIndex: number
): AdaptedDashboardComponent {
  const hasResults = Array.isArray(component.results) && component.results.length > 0;
  const geoJSON = hasResults && component.results ? component.results[0]?.geojson : null;

  if (hasResults && geoJSON) {
    const supportedFeatures = ['title', 'geoJSON data', 'boundaries', 'gradient colors', 'hoverLabel'];
    const unsupportedFeatures = ['width/height (uses container size)'];

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        'Map Region Density',
        'fully_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered as a choropleth map using pre-computed GeoJSON data.'
      ),
      precomputedData: {
        results: component.results!,
        visualization: { kind: 'Map Region Density' as any },
        title: extractTitle(component.title || 'Map Region Density'),
      },
    };
  }

  // No results available
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      'Map Region Density',
      'unsupported',
      [],
      ['no pre-computed results'],
      'Map Region Density requires pre-computed GeoJSON results from the dashboard API. Results not available.'
    ),
  };
}

/**
 * Extended PaymentDashboard type that may include pre-computed results from the distro dashboard API.
 */
type PaymentDashboardWithResults = survey.PaymentDashboard & {
  results?: any[];
  error?: string;
};

/**
 * Adapt PaymentDashboard to use pre-computed results.
 * PaymentDashboard returns an array of payment status rows from the backend.
 */
export function adaptPaymentDashboard(
  component: PaymentDashboardWithResults,
  componentIndex: number
): AdaptedDashboardComponent {
  const hasResults = Array.isArray(component.results);

  if (component.error) {
    return createQueryErrorResult(component, componentIndex, 'Payment Dashboard', component.error);
  }

  if (hasResults) {
    const supportedFeatures = ['title', 'payment status table', 'dimensions', 'friendlyDimensionNames'];
    const unsupportedFeatures = ['filter (already executed)', 'ExploreLink drill-down'];

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        'Payment Dashboard',
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered using pre-computed payment results.'
      ),
      precomputedData: {
        results: component.results!,
        visualization: { kind: 'Payment Dashboard' as any },
        title: 'Payment Dashboard',
      },
    };
  }

  // No results available
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      'Payment Dashboard',
      'unsupported',
      [],
      ['no pre-computed results'],
      'Payment Dashboard requires pre-computed results from the dashboard API. Results not available.'
    ),
  };
}

/**
 * Extended CustomQuery type that may include pre-computed results from the distro dashboard API.
 * When the dashboard is fetched, results are attached to each component.
 */
type CustomQueryWithResults = survey.CustomQuery & {
  results?: any[];
  error?: string;
  summaryKey?: string;
  groupKeys?: string[];
};

/**
 * Extract title from visualization config
 */
function getVisualizationTitle(viz: survey.CustomQuery['visualization']): string | undefined {
  if ('title' in viz && viz.title) {
    const title = viz.title;
    if (typeof title === 'string') return title;
    if (typeof title === 'object' && 'en' in title) return title.en;
  }
  return undefined;
}

/**
 * Adapt CustomQuery based on its visualization type.
 * If pre-computed results are available, we can render chart visualizations
 * using the precomputedData path (bypassing the translation API).
 */
export function adaptCustomQuery(
  component: CustomQueryWithResults,
  componentIndex: number
): AdaptedDashboardComponent {
  const vizKind = component.visualization?.kind || 'Unknown';

  if (component.error) {
    return createQueryErrorResult(component, componentIndex, `Custom Query (${vizKind})`, component.error);
  }

  const viz = component.visualization;
  const hasResults = Array.isArray(component.results);

  // Download-only Custom Query Table — no inline table, just a download card
  if (vizKind === 'Table' && (viz as any)?.download?.downloadOnly) {
    return createDownloadOnlyAdaptation(component, componentIndex,
      'Download-only Custom Query Table. Data available via CSV download.');
  }

  // Table visualization - check if we have results
  if (vizKind === 'Table' && hasResults) {
    const tableViz = component.visualization as survey.Table;
    const download = tableViz.download;
    const supportedFeatures = ['title', 'results data', 'auto-generated columns', 'view button', 'linksToSection'];
    if (download) supportedFeatures.push('download');
    const unsupportedFeatures = ['pdfDownload', 'showAllSimultaneously'];

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        `Custom Query (${vizKind})`,
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered as a basic table. PDF generation is not supported.'
      ),
      precomputedData: {
        results: component.results!,
        visualization: component.visualization,
        title: getVisualizationTitle(component.visualization),
      },
      download: download ? {
        filename: download.filename,
        downloadOnly: download.downloadOnly,
        asyncDownload: download.asyncDownload,
        downloadPath: download.downloadPath,
      } : undefined,
    };
  }

  // Table visualization without results (rendered via deferred /load).
  // Still forward download config so the download action is available even
  // before inline results are fetched (mirrors adaptApplicantTable).
  if (vizKind === 'Table') {
    const download = (component.visualization as survey.Table).download;
    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        `Custom Query (${vizKind})`,
        download ? 'partially_supported' : 'unsupported',
        download ? ['download'] : [],
        ['no pre-computed results'],
        'Custom Query Table requires pre-computed results from the dashboard API. Results not available.'
      ),
      download: download ? {
        filename: download.filename,
        downloadOnly: download.downloadOnly,
        asyncDownload: download.asyncDownload,
        downloadPath: download.downloadPath,
      } : undefined,
    };
  }

  // For Custom Number, results is a single value (not an array)
  // Check if we have any results (could be number, string, or array)
  const hasAnyResults = component.results !== undefined && component.results !== null;

  // For chart visualizations (bar/pie), results must be an array
  if (hasResults && ['Custom Bar Chart', 'Custom Pie Chart'].includes(vizKind)) {
    const supportedFeatures = ['title', 'visualization', 'pre-computed results'];
    const unsupportedFeatures = ['viewFilters', 'refresh'];

    return {
      original: component,
      adapted: null, // We use precomputedData instead of adapted
      audit: createAudit(
        componentIndex,
        `Custom Query (${vizKind})`,
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered using pre-computed results. ViewFilters and refresh are not supported.'
      ),
      precomputedData: {
        results: component.results!,
        visualization: component.visualization,
        title: getVisualizationTitle(component.visualization),
      },
    };
  }

  // For Custom Number, results is a single value (not an array)
  if (hasAnyResults && vizKind === 'Custom Number') {
    const supportedFeatures = ['title', 'visualization', 'pre-computed results', 'prefix', 'suffix', 'useCommas', 'decimalPlaces'];
    const unsupportedFeatures = ['viewFilters', 'refresh'];

    return {
      original: component,
      adapted: null,
      audit: createAudit(
        componentIndex,
        `Custom Query (${vizKind})`,
        'partially_supported',
        supportedFeatures,
        unsupportedFeatures,
        'Rendered using pre-computed results. ViewFilters and refresh are not supported.'
      ),
      precomputedData: {
        // Results can be a single value for Custom Number
        results: component.results!, // We checked hasAnyResults above
        visualization: component.visualization,
        title: getVisualizationTitle(component.visualization),
      },
    };
  }

  // No results available - mark as unsupported with guidance
  switch (vizKind) {
    case 'Custom Number':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          `Custom Query (${vizKind})`,
          'unsupported',
          [],
          ['no pre-computed results'],
          'Custom Number requires pre-computed results from the dashboard API. Results not available.'
        ),
      };

    case 'Custom Bar Chart':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          `Custom Query (${vizKind})`,
          'unsupported',
          [],
          ['no pre-computed results'],
          'Custom Bar Chart requires pre-computed results from the dashboard API. Results not available.'
        ),
      };

    case 'Custom Pie Chart':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          `Custom Query (${vizKind})`,
          'unsupported',
          [],
          ['no pre-computed results'],
          'Custom Pie Chart requires pre-computed results from the dashboard API. Results not available.'
        ),
      };

    default:
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          `Custom Query (${vizKind})`,
          'unsupported',
          [],
          ['unknown visualization type'],
          `Custom Query with unknown visualization type: ${vizKind}`
        ),
      };
  }
}

/**
 * Adapt DashboardReportReference to DashboardReport
 */
export function adaptReportReference(
  component: survey.DashboardReportReference,
  componentIndex: number
): AdaptedDashboardComponent {
  const supportedFeatures = ['reportId'];
  const unsupportedFeatures: string[] = [];

  const adapted: DashboardReport = {
    kind: 'Report',
    title: 'Report',
    reportId: component.reportId,
  };

  return {
    original: component,
    adapted,
    audit: createAudit(
      componentIndex,
      'Report',
      'fully_supported',
      supportedFeatures,
      unsupportedFeatures
    ),
  };
}

/**
 * Create an unsupported component result
 */
function createUnsupportedResult(
  component: survey.DashboardComponent,
  componentIndex: number,
  kind: string,
  reason: string
): AdaptedDashboardComponent {
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      kind,
      'unsupported',
      [],
      ['entire component'],
      reason
    ),
  };
}

/**
 * Helper to create a standard error result for components whose query failed.
 */
function createQueryErrorResult(
  component: survey.DashboardComponent,
  componentIndex: number,
  kind: string,
  errorMessage: string
): AdaptedDashboardComponent {
  return {
    original: component,
    adapted: null,
    audit: createAudit(
      componentIndex,
      kind,
      'unsupported',
      [],
      ['query error'],
      sanitizeDashboardError(errorMessage)
    ),
  };
}

/**
 * Main adapter function that routes to specific adapters based on component kind.
 * Returns an AdaptedDashboardComponent with either the adapted component or null
 * if the component is unsupported.
 */
export function adaptComponent(
  component: survey.DashboardComponent,
  componentIndex: number
): AdaptedDashboardComponent {
  const kind = 'kind' in component ? component.kind : 'Unknown';

  switch (kind) {
    case 'Hero Number':
      return adaptHeroNumber(component as survey.HeroNumberWithFields, componentIndex);

    case 'Bar Chart':
      return adaptBarChart(component as survey.BarChartWithFields, componentIndex);

    case 'Pie Chart':
      return adaptPieChart(component as survey.PieChartWithFields, componentIndex);

    case 'Line Chart':
      return adaptLineChart(component as survey.LineChart, componentIndex);

    case 'Summary Table':
      return adaptSummaryTable(component as survey.SummaryTable, componentIndex);

    case 'Map':
      return adaptMap(component as survey.Map, componentIndex);

    case 'Hero Count':
      return adaptHeroCount(component as survey.HeroCountWithFields, componentIndex);

    case 'Dashboard Section':
      return createUnsupportedResult(
        component,
        componentIndex,
        kind,
        'Dashboard Section (nested components with filters) is not supported. Child components are rendered separately.'
      );

    case 'Applicant Table':
      return adaptApplicantTable(component as ApplicantTableWithResults, componentIndex);

    case 'Custom Query':
      return adaptCustomQuery(component as survey.CustomQuery, componentIndex);

    case 'Queue':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          'Queue',
          'fully_supported',
          ['title', 'condition', 'assigneeField', 'startSection'],
          [],
          'Rendered as a QueueCard with live stats and claim functionality.'
        ),
      };

    case 'Map Region Density':
      return adaptMapRegionDensity(component as MapRegionDensityWithResults, componentIndex);

    case 'Static Widget Reference': {
      const isUnresolved = (component as any)._unresolved === true;
      if (isUnresolved) {
        return {
          original: component,
          adapted: null,
          audit: createAudit(componentIndex, 'Static Widget Reference', 'unsupported',
            [], [],
            'Unable to resolve Static Widget. It may have been deleted from the widget library.'),
        };
      }
      return {
        original: component,
        adapted: null,
        audit: createAudit(componentIndex, 'Static Widget Reference', 'fully_supported',
          ['staticWidgetId'], [], 'Resolved at fetch time via the widget library.'),
      };
    }
    case 'Report Reference':
      return adaptReportReference(component as survey.DashboardReportReference, componentIndex);

    case 'Payment Dashboard':
      return adaptPaymentDashboard(component as PaymentDashboardWithResults, componentIndex);

    case 'Data Dictionary':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          'Data Dictionary',
          'fully_supported',
          ['limitToSubsurvey', 'excludeFields', 'includeComputations', 'excludeKinds', 'type'],
          [],
          'Rendered as a DataDictionary download button.'
        ),
      };

    case 'Dashboard Explanation': {
      const adapted: DashboardExplanation = {
        kind: 'Dashboard Explanation',
        content: (component as survey.DashboardExplanation).content,
        width: (component as survey.DashboardExplanation).width,
        alignment: (component as survey.DashboardExplanation).alignment,
      };
      return {
        original: component,
        adapted,
        audit: createAudit(
          componentIndex,
          'Dashboard Explanation',
          'fully_supported',
          ['content', 'width', 'alignment'],
          [],
          'Rendered as markdown content block.'
        ),
      };
    }

    case 'Subsurvey Summary':
      return {
        original: component,
        adapted: null,
        audit: createAudit(
          componentIndex,
          'Subsurvey Summary',
          'partially_supported',
          ['expansion to charts'],
          ['download'],
          'Expanded into charts and tables by the server.'
        ),
      };

    default:
      return createUnsupportedResult(
        component,
        componentIndex,
        kind,
        `Unknown component kind: ${kind}`
      );
  }
}

/**
 * Adapt all components from a distro dashboard.
 * Also handles DashboardSection by flattening child components.
 */
export function adaptDashboardComponents(
  components: survey.DashboardComponent[]
): AdaptedDashboardComponent[] {
  const results: AdaptedDashboardComponent[] = [];
  let globalIndex = 0;

  function processComponent(
    component: survey.DashboardComponent,
    parentSection?: { filterPath?: string; filters?: { name: string; filter: any }[] }
  ) {
    const results: AdaptedDashboardComponent[] = [];
    const kind = 'kind' in component ? component.kind : 'Unknown';

    // Special handling for Dashboard Section: render as section header, then process children
    if (kind === 'Dashboard Section') {
      const section = component as survey.DashboardSection;

      // Process child components (flatten into main list), tagging with parent section info
      const sectionInfo = section.filters?.length
        ? { filterPath: section.filterPath, filters: section.filters }
        : parentSection;
      const children = section.components && Array.isArray(section.components)
        ? section.components.flatMap(child => processComponent(child, sectionInfo))
        : [];

      // Add entry for the section header (rendered as a divider with optional filter tabs)
      results.push({
        original: component,
        adapted: null,
        audit: createAudit(
          globalIndex++,
          'Dashboard Section',
          'partially_supported',
          ['title', 'section header', 'filters'],
          ['showAllSimultaneously'],
          'Rendered as a section header with filter tabs.'
        ),
        sectionFilters: section.filters,
        sectionFilterPath: section.filterPath,
        children,
      });
    } else if (kind === 'Templated Block') {
      const expanded = expandGenericTemplates([component]);
      for (const expandedComp of expanded) {
        results.push(...processComponent(expandedComp, parentSection));
      }
    } else {
      const result = adaptComponent(component, globalIndex++);
      // Tag children with parent section filter info
      if (parentSection) {
        result.parentSectionFilterPath = parentSection.filterPath;
        result.parentSectionFilters = parentSection.filters;
      }
      results.push(result);
    }

    return results;
  }

  for (const component of components) {
    results.push(...processComponent(component));
  }

  return results;
}

